import { CommunityPost, CommunityReply } from '@api/communityPostsApiHandler';
import { useAppSelector } from '@hooks/useAppSelector';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { Skeleton } from '@mui/material';
import { MotionText } from '@shared/atoms';
import { SystemButton } from '@shared/molecules/SystemButton/SystemButton';
import { NoDataPlaceholder } from '@shared/pages/SharePage/NoDataPlaceholder';
import { motion } from 'framer-motion';
import React, { useCallback, useMemo } from 'react';
import { RootState } from 'store';
import { PostCard } from '../PostCard/PostCard';
import styles from './index.module.scss';

interface CommunityFeedProps {
  posts: CommunityPost[];
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean | undefined;
  expandedPostId: number | null;
  onToggleExpand: (postId: number, hasReplies: boolean) => void;
  onCreatePost: () => void;
  repliesData: Record<number, CommunityReply[]>;
  loadingReplies: Record<number, boolean>;
  refetchReplies: (postId: number) => void;
  /** Override default empty feed placeholder (e.g. single-post deep link not found). */
  emptyState?: React.ReactNode;
  /** Optional action below the posts list (e.g. link back to full community feed). */
  footerAction?: React.ReactNode;
  /** Hide the "You've reached the end!" message (single-post view). */
  hideEndOfFeed?: boolean;
  /** Number of skeleton cards while loading (default: feed scroll). */
  skeletonCount?: number;
  /** Called when a post is deleted from the feed (single-post deep link). */
  onPostDeleted?: () => void;
}

export const CommunityFeed: React.FC<CommunityFeedProps> = ({
  posts,
  isLoading,
  isFetchingMore,
  hasMore,
  expandedPostId,
  onToggleExpand,
  onCreatePost,
  repliesData,
  loadingReplies,
  refetchReplies,
  emptyState,
  footerAction,
  hideEndOfFeed = false,
  skeletonCount = 10,
  onPostDeleted,
}) => {
  const skeletonItems = useMemo(
    () => Array.from({ length: skeletonCount }, (_, index) => index + 1),
    [skeletonCount],
  );
  const { isDesktop } = useWindowDimensions();
  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig);

  const handleToggleExpand = useCallback(
    (postId: number, repliesCount: number) => {
      onToggleExpand(postId, repliesCount > 0);
    },
    [onToggleExpand],
  );

  const communityHeader = (
    <div className={styles.communityHeader}>
      <div className={styles.communityHeaderContent}>
        <MotionText as="h3" text="Community" className={styles.communityTitle} />
        <MotionText
          as="text"
          text={
            companyConfigData?.data?.communityWelcomeText ||
            'Share thoughts, ask questions, and connect with others in your organisation. '
          }
          className={styles.communitySubtitle}
        />
      </div>
      <SystemButton
        className={styles.addPostBtn}
        onClick={onCreatePost}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        fullWidthButton={!isDesktop}
        size="medium"
      >
        Add Post
      </SystemButton>
    </div>
  );

  const postsContent = isLoading ? (
    <div className={styles.skeletonsContainer}>
      {skeletonItems.map((index) => (
        <div key={index} className={styles.postCard}>
          <div className={styles.postHeader}>
            <Skeleton variant="circular" width={40} height={40} />
            <div className={styles.postHeaderInfo}>
              <Skeleton variant="text" width={150} height={24} />
              <Skeleton variant="text" width={100} height={20} />
            </div>
          </div>
          <Skeleton variant="rectangular" height={80} style={{ marginBottom: 16 }} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="60%" />
        </div>
      ))}
    </div>
  ) : posts.length === 0 ? (
    emptyState ?? <NoDataPlaceholder feedType="community" />
  ) : (
    <>
      {posts.map((post) => (
        <PostCard
          key={post.id}
          post={post}
          isExpanded={expandedPostId === post.id}
          onToggleExpand={() => handleToggleExpand(post.id, post.repliesCount)}
          replies={repliesData[post.id] || []}
          loadingReplies={loadingReplies[post.id] || false}
          refetchReplies={refetchReplies}
          onPostDeleted={onPostDeleted}
        />
      ))}

      {isFetchingMore && (
        <motion.div
          className={styles.loadingMore}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className={styles.postCard}>
            <div className={styles.postHeader}>
              <Skeleton variant="circular" width={40} height={40} />
              <div className={styles.postHeaderInfo}>
                <Skeleton variant="text" width={150} height={24} />
                <Skeleton variant="text" width={100} height={20} />
              </div>
            </div>
            <Skeleton variant="rectangular" height={60} style={{ marginBottom: 16 }} />
            <Skeleton variant="text" width="100%" />
          </div>
        </motion.div>
      )}

      {!hideEndOfFeed && !hasMore && posts.length > 0 && (
        <motion.div className={styles.endOfFeed} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <MotionText as="text" text="You've reached the end!" />
        </motion.div>
      )}
    </>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      className={styles.community}
    >
      {communityHeader}
      <div className={styles.communityFeed}>
        <div className={styles.postsContainer}>{postsContent}</div>
        {footerAction ? <div className={styles.footerAction}>{footerAction}</div> : null}
      </div>
    </motion.div>
  );
};

CommunityFeed.defaultProps = {
  emptyState: undefined,
  footerAction: undefined,
  hideEndOfFeed: false,
  skeletonCount: 10,
  onPostDeleted: undefined,
};
